import React from 'react';
import { Box, Typography, Button, Paper, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export const JobNimbusHelp: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/settings/integrations/jobnimbus/setup')}
          variant="outlined"
        >
          Back to Setup
        </Button>
        <Typography variant="h4" component="h1">
          JobNimbus Integration Help
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Get your JobNimbus API Key
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          To connect Paiv with JobNimbus, you&apos;ll need to generate an API
          key from your JobNimbus account:
        </Typography>
        <Box component="ol" sx={{ pl: 3 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Log into your JobNimbus account
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Navigate to Settings → Integrations → API
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Click &quot;New API key&quot;
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Copy the generated API key and paste it into the setup form
            </Typography>
          </li>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Add the Paiv Webhook in JobNimbus
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          After saving your integration, you&apos;ll receive a webhook URL that
          needs to be configured in JobNimbus. The webhook setup depends on your
          Main Object Type setting:
        </Typography>

        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontSize: '1.1rem' }}>
          If your Main Object Type is &quot;Contact&quot;:
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Set up one webhook for Contacts:
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Copy the webhook URL provided after saving your integration
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              In JobNimbus, go to Settings → Automations
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Click &quot;Add Automation&quot; → Name it &quot;Update Paiv
              Contact Deal&quot; → Trigger Type: Event based → When a Contact is
              Created or Modified → Add a condition: If Type is Customer → Save
              → Add Action → Select Webhook: → Continue → then add the webhook
              URL
            </Typography>
          </li>
        </Box>

        <Typography variant="h6" sx={{ mt: 3, mb: 1, fontSize: '1.1rem' }}>
          If your Main Object Type is &quot;Job&quot;:
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Set up two webhooks - one for Contacts and one for Jobs:
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
          Contact Webhook:
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Copy the webhook URL provided after saving your integration
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              In JobNimbus, go to Settings → Automations
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Click &quot;Add Automation&quot; → Name it &quot;Update Paiv
              Contact Deal&quot; → Trigger Type: Event based → When a Contact is
              Created or Modified → Add a condition: If Type is Customer → Save
              → Add Action → Select Webhook: → Continue → then add the webhook
              URL
            </Typography>
          </li>
        </Box>
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 'medium' }}>
          Job Webhook:
        </Typography>
        <Box component="ol" sx={{ pl: 3 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Copy the same webhook URL (you&apos;ll use it for both webhooks)
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Click &quot;Add Automation&quot; → Name it &quot;Update Paiv
              Deal&quot; → Trigger Type: Event based → When a Job is Created or
              Modified → Add a condition: If Type is Insurance (or &quot;1A.
              Insurance Sales&quot; - whatever your blue board/workflow is
              called) → Save → Add Action → Select Webhook: → Continue → then
              add the webhook URL
            </Typography>
          </li>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Map JobNimbus Stages to Paiv Stages
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Once you&apos;ve entered your API key and fetched stages, you can map
          JobNimbus stages to your Paiv stages:
        </Typography>
        <Box component="ol" sx={{ pl: 3 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Click &quot;Fetch JobNimbus Stages&quot; to load your stages
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              For each JobNimbus stage, select the corresponding Paiv stage from
              the dropdown
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Save your integration to apply the stage mappings
            </Typography>
          </li>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Troubleshooting
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          If you&apos;re experiencing issues with your JobNimbus integration:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Verify your API key is correct and has the necessary permissions
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Ensure the webhook URL is properly configured in JobNimbus
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Check that your stage mappings are complete
            </Typography>
          </li>
          <li>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Contact support if you continue to experience issues
            </Typography>
          </li>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Need More Help?
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          If you need additional assistance with your JobNimbus integration,
          please contact our support team:
        </Typography>
        <Typography variant="body1">
          Email: <Link href="mailto:support@paiv.ai">support@paiv.ai</Link>
        </Typography>
      </Paper>
    </Box>
  );
};
